import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createWarehouse,
  deactivateWarehouse,
  listWarehouses,
  updateWarehouse,
  type WarehouseRow,
} from "@/shared/api/inventory";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { warehouseColumns } from "./warehouses-columns";

const warehouseCreateSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  code: z.string().trim().optional(),
});
type WarehouseCreateInput = z.input<typeof warehouseCreateSchema>;

/** Warehouse CRUD — plan/09 multi-WH default 1; plan/20 `/warehouses` */
export function WarehousesPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const confirmUi = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const warehousesQuery = useQuery({
    queryKey: queryKeys.warehouses.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listWarehouses(companySlug!, { includeDeactivated: true }),
  });
  const errorMsg = queryErrorMessage(
    warehousesQuery.error,
    warehousesQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.warehouses.all(companySlug),
    });
  }

  const createForm = useForm<WarehouseCreateInput>({
    resolver: zodResolver(warehouseCreateSchema),
    defaultValues: { name: "", code: "" },
  });

  const create = useMutation({
    mutationFn: async (raw: WarehouseCreateInput) => {
      const body = warehouseCreateSchema.parse(raw);
      return createWarehouse(companySlug!, {
        name: body.name,
        ...(body.code ? { code: body.code } : {}),
      });
    },
    onSuccess: async () => {
      createForm.reset({ name: "", code: "" });
      setError(null);
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const columns = useMemo(
    () =>
      warehouseColumns({
        busy,
        onRename: (w: WarehouseRow, nextName: string) => {
          const trimmed = nextName.trim();
          if (!trimmed || trimmed === w.name || !companySlug) return;
          setBusy(true);
          setError(null);
          void updateWarehouse(companySlug, w.id, { name: trimmed })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Update failed"),
            )
            .finally(() => setBusy(false));
        },
        onSetDefault: (w: WarehouseRow) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updateWarehouse(companySlug, w.id, {
            isDefault: true,
            reactivate: w.deactivatedAt != null,
          })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
        onDeactivate: (w: WarehouseRow) => {
          void (async () => {
            if (!companySlug) return;
            const ok = await confirmUi({
              title: `Deactivate “${w.name}”?`,
              description:
                "It will hide from new picks. Existing stock stays; you can reactivate later.",
              confirmLabel: "Deactivate",
              destructive: true,
            });
            if (!ok) return;
            setBusy(true);
            setError(null);
            try {
              await deactivateWarehouse(companySlug, w.id);
              await reload();
            } catch (err: unknown) {
              setError(err instanceof Error ? err.message : "Failed");
            } finally {
              setBusy(false);
            }
          })();
        },
        onReactivate: (w: WarehouseRow) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updateWarehouse(companySlug, w.id, { reactivate: true })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [busy, companySlug, confirmUi],
  );

  if (!companySlug) return null;

  const warehouses = warehousesQuery.data?.warehouses ?? [];
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
    <PageFrame>
      <PageHeader
        title="Warehouses"
        description="Default is Main. Add more when you stock in more than one place — pickers stay hidden until then."
      />

      {warehousesQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading warehouses…
        </Alert>
      )}
      {!warehousesQuery.isPending && warehouses.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No warehouses"
            description="Add Main or another location to hold on-hand stock."
          />
        </div>
      ) : null}
      {!warehousesQuery.isPending && warehouses.length > 0 ? (
        <DataTable
          listId="warehouses"
          companySlug={companySlug}
          columns={columns}
          data={warehouses}
          getRowId={(r) => r.id}
          manualSorting={false}
        />
      ) : null}

      <form
        className="mt-8 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={createForm.handleSubmit((values) => create.mutate(values))}
      >
        <h2 className="m-0 text-sm font-semibold">Add warehouse</h2>
        <div>
          <Label htmlFor="wh-name">Name</Label>
          <Input
            id="wh-name"
            className="mt-1"
            placeholder="e.g. Backroom"
            {...createForm.register("name")}
          />
          {createForm.formState.errors.name && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">
              {createForm.formState.errors.name.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="wh-code">Code (optional)</Label>
          <Input
            id="wh-code"
            className="mt-1 font-mono"
            placeholder="from name if blank"
            {...createForm.register("code")}
          />
        </div>
        <Button type="submit" disabled={busy || create.isPending}>
          Add
        </Button>
      </form>

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <p className="mt-8">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
